import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from '@hooks/useTranslation';
import type { TabItem } from './types';
import styles from './DraggableTabs.module.scss';

// 6-dot grip icon for drag handle
function GripIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="4" cy="2" r="1" fill="currentColor" />
      <circle cx="8" cy="2" r="1" fill="currentColor" />
      <circle cx="4" cy="6" r="1" fill="currentColor" />
      <circle cx="8" cy="6" r="1" fill="currentColor" />
      <circle cx="4" cy="10" r="1" fill="currentColor" />
      <circle cx="8" cy="10" r="1" fill="currentColor" />
    </svg>
  );
}

// X icon for remove
function CloseIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface DraggableTabProps<TTab extends TabItem> {
  tab: TTab;
  isActive: boolean;
  isDraggable: boolean;
  isRemovable: boolean;
  isRemoved: boolean;
  onClick: () => void;
  onRemove?: () => void;
  onUndoRemove?: () => void;
}

export function DraggableTab<TTab extends TabItem>({
  tab,
  isActive,
  isDraggable,
  isRemovable,
  isRemoved,
  onClick,
  onRemove,
  onUndoRemove,
}: DraggableTabProps<TTab>) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: tab.id,
    disabled: !isDraggable || isRemoved,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const classNames = [
    styles.tab,
    isActive && !isRemoved ? styles['tab--active'] : '',
    isDragging ? styles['tab--dragging'] : '',
    isRemoved ? styles['tab--removed'] : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleRemoveClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    onRemove?.();
  };

  const handleUndoClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    onUndoRemove?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  return (
    <button
      ref={setNodeRef}
      style={style}
      className={classNames}
      onClick={isRemoved ? undefined : onClick}
      type="button"
      title={tab.label}
      {...(isRemoved ? {} : attributes)}
      {...(isRemoved ? {} : listeners)}
    >
      {isDraggable && !isRemoved && (
        <span className={styles.tab__grip}>
          <GripIcon />
        </span>
      )}
      <span className={styles.tab__label}>{tab.label}</span>
      {isRemovable && !isDragging && !isRemoved && (
        <span
          role="button"
          tabIndex={0}
          className={styles.tab__close}
          aria-label={`Remove ${tab.label}`}
          onClick={handleRemoveClick}
          onKeyDown={(e) => handleKeyDown(e, () => onRemove?.())}
        >
          <CloseIcon />
        </span>
      )}
      {isRemoved && (
        <span
          role="button"
          tabIndex={0}
          className={styles.tab__undo}
          aria-label={`${t('K_BUTTON_UNDO', 'Undo')} ${tab.label}`}
          onClick={handleUndoClick}
          onKeyDown={(e) => handleKeyDown(e, () => onUndoRemove?.())}
        >
          {t('K_BUTTON_UNDO', 'Undo')}
        </span>
      )}
    </button>
  );
}
